import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Dialog from '@mui/material/Dialog';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { contractApi, customerApi, estimateApi } from '../services/contractsApi';
import { useAuth } from '../auth/AuthContext.jsx';

const CommandPalette = () => {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState({ customers: [], estimates: [], contracts: [] });

  useEffect(() => {
    const onKey = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setOpen((prev) => !prev);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (!open || !isAdmin) return undefined;
    let cancelled = false;
    Promise.allSettled([
      customerApi.getCustomers(),
      estimateApi.getEstimates(),
      contractApi.getContracts(),
    ]).then((settled) => {
      if (cancelled) return;
      setResults({
        customers: settled[0].status === 'fulfilled' ? settled[0].value.customers || [] : [],
        estimates: settled[1].status === 'fulfilled' ? settled[1].value.estimates || [] : [],
        contracts: settled[2].status === 'fulfilled' ? settled[2].value.contracts || [] : [],
      });
    });
    return () => {
      cancelled = true;
    };
  }, [open, isAdmin]);

  const matches = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return [];
    const items = [
      ...results.customers.map((item) => ({
        type: 'Customer',
        label: `${item.firstName || ''} ${item.lastName || ''}`.trim() || item.email,
        hint: item.email,
        to: `/customers/edit/${item._id || item.id}`,
      })),
      ...results.estimates.map((item) => ({
        type: 'Estimate',
        label: item.estimateNumber || item.title || 'Estimate',
        hint: item.propertyAddress || item.status,
        to: `/estimates/${item._id}`,
      })),
      ...results.contracts.map((item) => ({
        type: 'Job',
        label: item.contractNumber || item.title || 'Job',
        hint: item.propertyAddress || item.status,
        to: `/contracts/${item._id}`,
      })),
    ];
    return items
      .filter((item) => `${item.label} ${item.hint} ${item.type}`.toLowerCase().includes(term))
      .slice(0, 12);
  }, [query, results]);

  if (!isAdmin) return null;

  return (
    <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
      <TextField
        autoFocus
        placeholder="Search customers, addresses, estimates, jobs"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        sx={{ m: 2 }}
      />
      <List>
        {matches.map((item) => (
          <ListItemButton
            key={`${item.type}-${item.to}`}
            onClick={() => {
              setOpen(false);
              navigate(item.to);
            }}
          >
            <ListItemText primary={item.label} secondary={`${item.type} · ${item.hint || ''}`} />
          </ListItemButton>
        ))}
        {query && matches.length === 0 && (
          <Typography sx={{ px: 3, pb: 2 }} color="text.secondary">
            No matches
          </Typography>
        )}
      </List>
    </Dialog>
  );
};

export default CommandPalette;
